import { z } from 'zod';
import { emailSchema, nonEmptyText, optionalText } from './common';

/**
 * Gestão de usuários por quem pode adicioná-los (superadministração, gestão institucional e orientação). Toda
 * exclusão exige digitar uma frase com o nome da pessoa: um clique por engano não apaga nada.
 */

/** Frase que confirma a exclusão (ex.: "excluir Maria Souza"). */
export const deletionPhrase = (name: string) => `excluir ${name.trim().replace(/\s+/g, ' ')}`;

/** Sem diferença de maiúsculas, acentos e espaços extras ("Prof.ª" também vale como "prof.a"). */
const normalizePhrase = (s: string) =>
  s
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/** A frase digitada confere com a de exclusão desta pessoa? */
export const confirmsDeletion = (typed: string, name: string) => normalizePhrase(typed) === normalizePhrase(deletionPhrase(name));

export const deleteConfirmationSchema = z.object({
  /** A frase de `deletionPhrase(nome)`, digitada pela pessoa */
  confirmation: z.string().trim().min(1, 'Digite a frase de confirmação').max(400),
  /** Motivo (ex.: número do pedido LGPD), registrado na auditoria */
  reason: optionalText(500),
});
export type DeleteConfirmationInput = z.input<typeof deleteConfirmationSchema>;
export type DeleteConfirmationData = z.output<typeof deleteConfirmationSchema>;

const personName = nonEmptyText(160, 'Nome');
/** Texto livre informativo; vazio apaga */
const profileText = z
  .string()
  .trim()
  .max(200, 'Máximo de 200 caracteres')
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

/** Superadministração: conta nova (a pessoa define a senha por um link de uso único). */
export const adminCreateUserSchema = z.object({
  name: personName,
  email: emailSchema,
  /** Já habilita como orientador(a) (cria o espaço de orientação) */
  advisor: z.boolean().default(false),
});
export type AdminCreateUserInput = z.input<typeof adminCreateUserSchema>;
export type AdminCreateUserData = z.output<typeof adminCreateUserSchema>;

/** Superadministração: dados da conta e situação (desativada não entra). */
export const adminUpdateUserSchema = z.object({
  name: personName,
  email: emailSchema,
  institutionName: profileText,
  programName: profileText,
  status: z.enum(['ACTIVE', 'DISABLED']),
});
export type AdminUpdateUserInput = z.input<typeof adminUpdateUserSchema>;
export type AdminUpdateUserData = z.output<typeof adminUpdateUserSchema>;

/** Gestão institucional: nome e e-mail de um membro (só de quem atua apenas nas instituições que a pessoa administra). */
export const memberUpdateSchema = z.object({ name: personName, email: emailSchema });
export type MemberUpdateInput = z.infer<typeof memberUpdateSchema>;
